import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Search, MapPin, User, Users, Plus, Menu, ChevronDown, MessageSquare, LogOut, Grid, Settings, Shield, Loader2, BookOpen, BarChart3, Heart } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useMarketplace } from '@/components/site/marketplace-context';

import logoImg from '@/assets/logo.png';
import CitySelectionModal from '@/components/site/CitySelectionModal';

export const Header: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { city, setCity, isDetectingLocation } = useMarketplace();
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.get('/api/messages/unread-count')
      .then((data: any) => setUnreadCount(data?.count || 0))
      .catch(() => {});
  }, [isAuthenticated]);

  // Check if the user has a seller profile
  useEffect(() => {
    if (!isAuthenticated || !user?.sub) return;
    apiClient.get(`/api/sellers/user/${user.sub}`)
      .then((data: any) => {
        if (data?.id) setIsSeller(true);
      })
      .catch(() => setIsSeller(false));
  }, [isAuthenticated, user?.sub]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/listings?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/listings');
    }
  };


  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-1.5 md:gap-5 md:py-2">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center">
          <img
            src={logoImg}
            alt="MemAds Logo"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* City Select - Trigger Modal */}
        <div className="relative hidden md:block">
          <button
            type="button"
            onClick={() => setIsCityModalOpen(true)}
            className="flex items-center gap-2 h-11 cursor-pointer rounded-xl border border-border bg-background pl-3 pr-3 text-sm font-medium text-foreground transition hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          >
            {isDetectingLocation ? (
              <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
            ) : (
              <MapPin className="h-4 w-4 text-primary shrink-0" />
            )}
            <span className="max-w-[120px] truncate">{isDetectingLocation ? 'Detecting...' : city}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
          </button>
        </div>

        {/* Search Input */}
        <form onSubmit={handleSearch} className="relative flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Find cars, mobiles, properties and more..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11 w-full rounded-xl border border-border bg-secondary pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-2 focus:ring-primary/15"
            />
          </div>
          <button type="submit" className="hidden sm:inline-flex h-11 items-center justify-center rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-elevated transition hover:brightness-105">
            Search
          </button>
          <span className="hidden sm:inline-flex items-center text-muted-foreground font-light text-lg px-1">/</span>
          <Link to="/listings" className="hidden sm:inline-flex h-11 items-center justify-center rounded-xl border border-border bg-background px-6 text-sm font-semibold text-foreground transition hover:bg-secondary whitespace-nowrap">
            All Ads
          </Link>
        </form>

        {/* Action Links */}
        <div className="flex items-center gap-2">
          {/* Blog link */}
          <Link
            to="/blog"
            aria-label="Blog"
            className="relative hidden h-11 px-2.5 flex-col items-center justify-center rounded-xl text-foreground transition hover:bg-secondary md:inline-flex"
          >
            <BookOpen className="h-5 w-5" />
            <span className="text-[10px] font-semibold mt-0.5 leading-none whitespace-nowrap">Blog</span>
          </Link>



          {isAuthenticated ? (
            <>
              {/* Desktop links */}
              <Link
                to="/friends"
                aria-label="Find Friends"
                className="relative hidden h-11 w-12 flex-col items-center justify-center rounded-xl text-primary/80 transition hover:bg-secondary md:inline-flex px-1"
              >
                <Users className="h-5 w-5" />
                <span className="text-[10px] font-semibold mt-0.5 leading-none">Friends</span>
              </Link>
              <Link
                to="/messages"
                aria-label="Inbox"
                className="relative hidden h-11 w-12 flex-col items-center justify-center rounded-xl text-foreground transition hover:bg-secondary md:inline-flex px-1"
              >
                <MessageSquare className="h-5 w-5" />
                <span className="text-[10px] font-semibold mt-0.5 leading-none">Chat</span>
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/my-listings"
                aria-label="My Listings"
                className="relative hidden h-11 px-2.5 flex-col items-center justify-center rounded-xl text-foreground transition hover:bg-secondary md:inline-flex"
              >
                <Grid className="h-5 w-5" />
                <span className="text-[10px] font-semibold mt-0.5 leading-none whitespace-nowrap">My Listings</span>
              </Link>

              {/* User Dropdown */}
              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center gap-2 h-11 px-3 rounded-xl text-gray-700 hover:text-gray-900 transition ${
                    isUserMenuOpen ? 'bg-secondary text-gray-900' : 'hover:bg-secondary'
                  }`}
                >
                  <div className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-sm">
                    {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm font-medium">{user?.name || user?.email}</span>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                <div
                  className={`absolute right-0 top-full pt-1.5 w-48 z-50 transition-all duration-200 transform ${
                    isUserMenuOpen
                      ? 'visible opacity-100 translate-y-0 pointer-events-auto'
                      : 'invisible opacity-0 -translate-y-2 pointer-events-none'
                  }`}
                >
                  <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User size={16} /> Profile
                    </Link>
                    {isSeller && (
                      <Link
                        to="/seller-dashboard"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <BarChart3 size={16} /> Seller Dashboard
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Settings size={16} /> Settings
                    </Link>
                    {user?.role === 'ADMIN' && (
                      <Link
                        to="/admin"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-t"
                      >
                        <Shield size={16} /> Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2 border-t"
                    >
                      <LogOut size={16} /> Logout
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="hidden h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-foreground transition hover:bg-secondary md:inline-flex"
            >
              <User className="h-5 w-5" />
              <span>Login</span>
            </Link>
          )}



          {/* Post Ad Button */}
          <Link
            to={isAuthenticated ? "/post" : "/login"}
            className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-gradient-accent px-4 text-sm font-semibold text-accent-foreground shadow-elevated transition hover:brightness-105"
          >
            <Plus className="h-4 w-4" />
            <span>Post Ad</span>
          </Link>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-foreground hover:bg-secondary md:hidden"
            aria-label="Toggle Menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile Drawer (Routing Menu) */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-border bg-background px-4 py-3 space-y-2">
          {/* Mobile Location Button */}
          <button
            type="button"
            onClick={() => {
              setIsCityModalOpen(true);
              setIsMenuOpen(false);
            }}
            className="flex w-full items-center justify-between py-2.5 px-3 mb-2 rounded-xl bg-secondary/80 hover:bg-secondary text-sm font-medium text-foreground transition"
          >
            <div className="flex items-center gap-2">
              {isDetectingLocation ? (
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
              ) : (
                <MapPin className="h-4 w-4 text-primary" />
              )}
              <span>Location: <strong className="text-primary">{isDetectingLocation ? 'Detecting...' : city}</strong></span>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          <Link to="/listings" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
            Browse All Ads
          </Link>
          <Link to="/blog" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
            Blog
          </Link>
          {isAuthenticated ? (
            <>
              <Link to="/friends" className="block py-2 text-sm font-medium text-primary" onClick={() => setIsMenuOpen(false)}>
                Find Friends
              </Link>
              <Link to="/my-listings" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
                My Listings
              </Link>
              <Link to="/messages" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
                Messages
              </Link>
              <Link to="/profile" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
                Profile Settings
              </Link>
              {isSeller && (
                <Link to="/seller-dashboard" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
                  Seller Dashboard
                </Link>
              )}
              <button onClick={handleLogout} className="block w-full text-left py-2 text-sm font-medium text-red-600">
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="block py-2 text-sm font-medium text-foreground" onClick={() => setIsMenuOpen(false)}>
              Login / Sign Up
            </Link>
          )}
        </div>
      )}

      {/* City Selection Modal */}
      <CitySelectionModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        currentCity={city}
        onSelectCity={setCity}
      />
    </header>
  );
};

export default Header;
